/**
 * Comprobante imprimible de operación empresa (Carlos jun 28, 2026 — Etapa 2)
 *
 * URL: /empresas/comprobante/:orderId
 * Diseño optimizado para impresión / guardar como PDF desde el navegador.
 * Botón "📄 Imprimir / Guardar PDF" usa window.print().
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import empresaApi from '../utils/empresaApi';
import { BRAND } from '../brand';

const EmpresaComprobante = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    empresaApi
      .get(`/empresas/me/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Orden no encontrada'));
  }, [orderId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white border border-red-200 rounded-xl p-6 max-w-md">
          <p className="text-red-700 font-semibold">{error}</p>
          <Link to="/empresas/panel" className="text-emerald-700 hover:underline text-sm mt-2 inline-block">
            ← Volver al panel
          </Link>
        </div>
      </div>
    );
  }
  if (!order) return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando comprobante…</div>;

  // Documento tributario: si faltan datos legales de la marca, no lo emitimos.
  // Es más seguro devolver un mensaje que imprimir un comprobante con RUT en blanco.
  if (!BRAND.legalName || !BRAND.legalRut) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white border border-amber-300 rounded-xl p-6 max-w-md">
          <p className="text-amber-800 font-semibold">Comprobante no disponible</p>
          <p className="text-slate-600 text-sm mt-2">
            Los datos legales de {BRAND.name} aún no están configurados en la
            instancia. El comprobante no puede emitirse hasta que se completen.
          </p>
          <Link to="/empresas/panel" className="text-emerald-700 hover:underline text-sm mt-3 inline-block">
            ← Volver al panel
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white py-6">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .receipt-container { box-shadow: none !important; border: none !important; }
          @page { margin: 1.5cm; }
        }
      `}</style>
      <div className="max-w-2xl mx-auto px-4">
        {/* Toolbar (no se imprime) */}
        <div className="no-print flex items-center justify-between mb-3">
          <Link to="/empresas/panel" className="text-sm text-slate-600 hover:text-slate-900">← Panel</Link>
          <button
            onClick={() => window.print()}
            data-testid="emp-print-btn"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg"
          >
            📄 Imprimir / Guardar PDF
          </button>
        </div>

        {/* Comprobante */}
        <div className="receipt-container bg-white border border-slate-200 rounded-2xl shadow-sm p-7" data-testid="emp-comprobante">
          {/* Cabecera */}
          <div className="flex items-start justify-between pb-4 border-b-2 border-emerald-600 mb-4">
            <div>
              <p className="text-2xl font-black text-emerald-700">{BRAND.name}</p>
              <p className="text-[10px] text-slate-500">{BRAND.legalName.toUpperCase()} · RUT {BRAND.legalRut}</p>
              <p className="text-[10px] text-slate-500">Sujeto Obligado UAF — Ley 19.913</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 uppercase">Comprobante</p>
              <p className="font-mono font-bold text-slate-900">{order.short_no}</p>
              <p className="text-[10px] text-slate-500 mt-1">{order.created_at?.slice(0, 16).replace('T', ' ')}</p>
            </div>
          </div>

          {/* Datos empresa */}
          <Section title="Empresa emisora">
            <Row k="Razón social" v={order.empresa_razon_social} />
            <Row k="RUT" v={order.empresa_rut} />
          </Section>

          {/* Operación */}
          <Section title="Operación">
            <Row k="Tipo" v="Transferencia internacional Chile → Bolivia" />
            <Row k="Propósito" v={order.proposito || '—'} />
            <Row k="Estado" v={<span className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-semibold uppercase">{order.status}</span>} />
          </Section>

          {/* Montos */}
          <Section title="Detalle del cambio">
            <Row k="Monto enviado" v={<strong>${order.amount_clp.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP</strong>} />
            <Row k="Monto entregado" v={<strong className="text-emerald-700">{order.amount_bob.toLocaleString('es-BO')} BOB</strong>} />
            <Row k="Tasa efectiva" v={`1 CLP = ${order.effective_rate?.toFixed(6)} BOB`} />
          </Section>

          {/* Destinatario */}
          <Section title="Destinatario en Bolivia">
            <Row k="Nombre" v={order.recipient?.nombre} />
            <Row k="Banco" v={order.recipient?.banco} />
            <Row k="Cuenta" v={`${order.recipient?.cuenta} (${order.recipient?.tipo_cuenta})`} />
            {order.recipient?.ci && <Row k="CI" v={order.recipient.ci} />}
            {order.recipient?.telefono && <Row k="Teléfono" v={order.recipient.telefono} />}
          </Section>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-slate-200 text-[10px] text-slate-500 space-y-1">
            <p>Este comprobante NO es una boleta tributaria (DTE). La boleta electrónica oficial se emitirá según el ciclo de facturación acordado con {BRAND.name}.</p>
            <p>{BRAND.name} opera bajo Ley 19.913, Circular UAF N°62. Consultas: {BRAND.complianceEmail}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-3">
    <p className="text-xs font-bold uppercase text-slate-600 tracking-wide mb-1">{title}</p>
    <div className="text-sm space-y-1">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-2 py-1 border-b border-slate-100 last:border-b-0">
    <span className="text-slate-500 text-xs">{k}</span>
    <span className="text-slate-900 text-right text-sm">{v}</span>
  </div>
);

export default EmpresaComprobante;
